import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../hooks/hooks';
import { Project } from '../../../features/projects/types';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../components/shared/table-v2/types';
import { Lx } from '../../../i18n/consts';
import { StatusTag } from '../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../components/shared/delete-icon-button';
import { AvatarsList } from '../../../components/shared/avatars';
import { getEmployeeNames } from '../../../components/shared/avatars/helpers';
import { normalizeString } from '../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getStatusSortValue, getStatusValue } from '../../../utils/table-filters';
import styles from '../projects.module.scss';

interface UseProjectsTableOptions {
    isMobile: boolean;
    search: string;
    selectedStatus: number;
    canDelete: boolean;
    onDelete: (project: Project) => void;
}

export function useProjectsTable({
    isMobile,
    search,
    selectedStatus,
    canDelete,
    onDelete,
}: UseProjectsTableOptions): TiminatorTableSettings<Project> {
    const intl = useIntl();
    const projects = useAppSelector(store => store.projects.items);

    const filteredProjects = useMemo(() => (
        filterByStatusAndSearch(
            projects,
            selectedStatus,
            project => project.isActive,
            search,
            project => {
                const employeeNames = getEmployeeNames(project.employees);

                return buildSearchValues([
                    project.name,
                    project.client?.name,
                    project.projectManager?.name,
                    employeeNames,
                    getStatusValue(intl, project.isActive),
                ]);
            }
        )
    ), [projects, search, selectedStatus, intl]);

    const renderStatusCell = (project: Project) => (
        <StatusTag status={project.isActive ? 'active' : 'suspended'}>
            <span>
                <FormattedMessage id={project.isActive ? 'active' : 'suspended'} />
            </span>
        </StatusTag>
    );

    const renderDeleteButton = (project: Project) => (
        <DeleteIconButton
            onClick={(event) => {
                event.stopPropagation();
                onDelete(project);
            }}
        />
    );

    const columns = useMemo((): TiminatorTableColumn<Project>[] => {
        const maxVisibleEmployees = 7;
        const getEmployeesSortValue = (project: Project) => {
            return normalizeString(getEmployeeNames(project.employees));
        };

        const renderNameCell = (project: Project) => (
            <div className={styles.projectInfo}>
                <span className={styles.projectName} title={project.name}>{project.name}</span>
            </div>
        );

        const renderMobileInfo = (project: Project) => (
            <div className={styles.mobileInfo}>
                <span className={styles.mobileName}>{project.name}</span>
                <div className={styles.mobileMeta}>{project.client?.name ?? ''}</div>
                <div className={styles.mobileMeta}>{project.projectManager?.name ?? ''}</div>
                <div className={styles.mobileMeta}>
                    <AvatarsList items={project.employees} visibleCount={maxVisibleEmployees} />
                </div>
            </div>
        );

        if (isMobile) {
            return [
                {
                    id: 'mobileInfo',
                    label: intl.formatMessage({ id: Lx.General.PROJECT }),
                    width: '1fr',
                    format: renderMobileInfo,
                },
                {
                    id: 'status',
                    label: intl.formatMessage({ id: Lx.General.STATUS }),
                    align: 'right',
                    width: '25%',
                    sortable: true,
                    getSortValue: (project: Project) => getStatusSortValue(project.isActive),
                    format: renderStatusCell,
                },
                ...(canDelete ? [{
                    id: 'deleteButton',
                    align: 'right' as const,
                    width: '24px',
                    format: renderDeleteButton,
                }] : []),
            ];
        }

        return [
            {
                id: 'name',
                label: intl.formatMessage({ id: Lx.General.NAME }),
                width: '1fr',
                sortable: true,
                getSortValue: (project: Project) => normalizeString(project.name),
                format: renderNameCell,
            },
            {
                id: 'client',
                label: intl.formatMessage({ id: Lx.General.CLIENT }),
                width: '1fr',
                sortable: true,
                getSortValue: (project: Project) => normalizeString(project.client?.name ?? ''),
                format: (project: Project) => (
                    <span className={styles.projectClientText}>
                        {project.client?.name ?? ''}
                    </span>
                ),
            },
            {
                id: 'projectManager',
                label: intl.formatMessage({ id: Lx.General.PROJECT_MANAGER }),
                width: '1fr',
                sortable: true,
                getSortValue: (project: Project) => normalizeString(project.projectManager?.name ?? ''),
                format: (project: Project) => (
                    <span className={styles.projectManagerText}>
                        {project.projectManager?.name ?? ''}
                    </span>
                ),
            },
            {
                id: 'employees',
                label: intl.formatMessage({ id: Lx.General.EMPLOYEES }),
                width: '1fr',
                sortable: true,
                getSortValue: getEmployeesSortValue,
                format: (project: Project) => (
                    <AvatarsList items={project.employees} visibleCount={maxVisibleEmployees} />
                ),
            },
            {
                id: 'status',
                label: intl.formatMessage({ id: Lx.General.STATUS }),
                width: '160px',
                sortable: true,
                getSortValue: (project: Project) => getStatusSortValue(project.isActive),
                format: renderStatusCell,
            },
            ...(canDelete ? [{
                id: 'deleteButton',
                align: 'right' as const,
                width: '64px',
                format: renderDeleteButton,
            }] : []),
        ];
    }, [canDelete, isMobile, onDelete, intl]);

    const isEmpty = filteredProjects.length === 0;

    return {
        columns,
        data: filteredProjects,
        isEmpty,
    };
}
